import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { jwtDecode } from "jwt-decode";
import { UserProfile } from "interfaces/IUser";

interface JwtPayload {
  exp: number;
}

interface AuthState {
  token: string | null;
  tokenExpiration: number | null;
  setToken: (token: string | null) => void;
  role: string | null;
  setRole: (role: string | null) => void;
  userData: UserProfile | null;
  setUserData: (userData: UserProfile | null) => void;
  authProvider: string | null;
  setAuthProvider: (provider: string | null) => void;
  isLoggedIn: () => boolean;
  isTokenValid: () => boolean;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      tokenExpiration: null,
      role: null,
      userData: null,
      authProvider: null,
      isLoading: false,
      error: null,

      setToken: (token) => {
        if (token) {
          try {
            const decoded = jwtDecode<JwtPayload>(token);
            set({
              token,
              tokenExpiration: decoded.exp * 1000,
            });
          } catch (error) {
            console.error("Error al decodificar el token:", error);
            set({ token, tokenExpiration: null });
          }
        } else {
          set({ token: null, tokenExpiration: null });
        }
      },

      setRole: (role) => set({ role }),
      setUserData: (userData) => set({ userData }),

      setAuthProvider: (provider) => set({ authProvider: provider }),

      isLoggedIn: () => {
        const { token } = get();
        return !!token && get().isTokenValid();
      },

      isTokenValid: () => {
        const { token, tokenExpiration } = get();
        if (!token || !tokenExpiration) return false;

        return tokenExpiration > Date.now();
      },

      logout: () =>
        set({
          token: null,
          tokenExpiration: null,
          role: null,
          userData: null,
          authProvider: null,
        }),
    }),
    {
      name: "auth-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        tokenExpiration: state.tokenExpiration,
        role: state.role,
        userData: state.userData,
        authProvider: state.authProvider,
      }),
    }
  )
);
